import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Fingerprint, Plus, Trash2, Key, Copy, RefreshCw, Monitor, 
  Users, Link2, AlertTriangle, CheckCircle, Clock, Server
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BiometricSettings = ({ user, onLogout }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  // Dialog states
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [showMappingDialog, setShowMappingDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('setup');

  // Form states
  const [deviceSn, setDeviceSn] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [deviceLocation, setDeviceLocation] = useState('');
  const [empCode, setEmpCode] = useState('');
  const [empEmail, setEmpEmail] = useState('');

  useEffect(() => {
    fetchConfig();
    fetchUsers();
  }, []);

  const fetchConfig = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/biometric/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      toast.error('Failed to fetch biometric config');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (e) {}
  };

  const generateToken = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/admin/biometric/generate-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('New API token generated');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to generate token');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const addDevice = async (e) => {
    e.preventDefault();
    if (!deviceSn.trim()) { toast.error('Device serial number is required'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/admin/biometric/devices`, {
        device_sn: deviceSn, device_name: deviceName, location: deviceLocation
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Device registered');
      setShowDeviceDialog(false);
      setDeviceSn(''); setDeviceName(''); setDeviceLocation('');
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register device');
    }
  };

  const removeDevice = async (sn) => {
    if (!window.confirm(`Remove device ${sn}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/biometric/devices/${sn}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Device removed');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to remove device');
    }
  };

  const addMapping = async (e) => {
    e.preventDefault();
    if (!empCode.trim() || !empEmail) { toast.error('Fill all fields'); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/admin/biometric/mappings`, {
        employee_code: empCode, user_email: empEmail
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mapping added');
      setShowMappingDialog(false);
      setEmpCode(''); setEmpEmail('');
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add mapping');
    }
  };

  const removeMapping = async (code) => {
    if (!window.confirm(`Remove mapping for ${code}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/biometric/mappings/${code}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping removed');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to remove mapping');
    }
  };

  const clearUnknownPunches = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/biometric/unknown-punches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Unknown punches cleared');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to clear');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-900">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="flex-1 p-6 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const mappedEmails = (config?.mappings || []).map(m => m.user_email);

  return (
    <div className="flex min-h-screen bg-slate-900" data-testid="biometric-settings">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <div className="flex-1 p-6 overflow-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Fingerprint className="w-8 h-8 text-amber-400" />
              <div>
                <h1 className="text-2xl font-bold text-white">Biometric Integration</h1>
                <p className="text-slate-400 text-sm">Configure biometric machine & RFID card reader for auto-attendance</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {[
              { key: 'setup', label: 'Setup & API', icon: Key },
              { key: 'devices', label: 'Devices', icon: Server },
              { key: 'mappings', label: 'Employee Mapping', icon: Link2 },
              { key: 'logs', label: 'Activity Logs', icon: Clock },
              { key: 'rawlogs', label: 'Raw Debug Logs', icon: AlertTriangle },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'bg-amber-500 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Setup & API Tab */}
          {activeTab === 'setup' && (
            <div className="space-y-6">
              {/* API Token */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Key className="w-5 h-5 text-amber-400" />
                    API Token
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-400 text-sm">This Bearer token authenticates your biometric machine with the CRM. Configure it in your machine's API settings.</p>
                  
                  {config?.api_token ? (
                    <div className="flex items-center gap-2">
                      <Input 
                        value={config.api_token} 
                        readOnly 
                        className="bg-slate-900 border-slate-600 text-white font-mono"
                      />
                      <Button variant="outline" size="sm" onClick={() => copyToClipboard(config.api_token)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={generateToken} className="text-amber-400 border-amber-500/50">
                        <RefreshCw className="w-4 h-4 mr-1" /> Regenerate
                      </Button>
                    </div>
                  ) : (
                    <Button onClick={generateToken} className="bg-amber-500 hover:bg-amber-600">
                      <Key className="w-4 h-4 mr-2" /> Generate API Token
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Machine Configuration Guide */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-emerald-400" />
                    Machine Configuration
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-400 text-sm mb-4">Configure these settings on your biometric machine:</p>
                  <div className="bg-slate-900 rounded-lg p-4 space-y-3 font-mono text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Request Method:</span>
                      <span className="text-emerald-400">POST</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Authorization:</span>
                      <span className="text-emerald-400">Bearer Token</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Content-Type:</span>
                      <span className="text-emerald-400">application/json</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Send Data:</span>
                      <span className="text-emerald-400">Body</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">API URL:</span>
                      <div className="flex items-center gap-2">
                        <span className="text-amber-400">{BACKEND_URL}/api/biometric/attendance</span>
                        <button onClick={() => copyToClipboard(`${BACKEND_URL}/api/biometric/attendance`)} className="text-slate-500 hover:text-white">
                          <Copy className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Devices Tab */}
          {activeTab === 'devices' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-slate-400">Register your biometric machines and RFID readers. Only registered devices can send data.</p>
                <Button onClick={() => setShowDeviceDialog(true)} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Device
                </Button>
              </div>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Serial Number</TableHead>
                        <TableHead className="text-slate-400">Device Name</TableHead>
                        <TableHead className="text-slate-400">Location</TableHead>
                        <TableHead className="text-slate-400">Registered</TableHead>
                        <TableHead className="text-slate-400 w-16">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(config?.devices || []).length === 0 ? (
                        <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No devices registered</TableCell></TableRow>
                      ) : (
                        (config?.devices || []).map(device => (
                          <TableRow key={device.device_sn} className="border-slate-700">
                            <TableCell className="text-white font-mono">{device.device_sn}</TableCell>
                            <TableCell className="text-slate-300">{device.device_name || '—'}</TableCell>
                            <TableCell className="text-slate-300">{device.location || '—'}</TableCell>
                            <TableCell className="text-slate-400 text-sm">{device.registered_at?.split('T')[0]}</TableCell>
                            <TableCell>
                              <button onClick={() => removeDevice(device.device_sn)} className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Mappings Tab */}
          {activeTab === 'mappings' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-slate-400">Link machine employee codes to CRM user accounts.</p>
                <Button onClick={() => setShowMappingDialog(true)} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" /> Add Mapping
                </Button>
              </div>
              
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Employee Code</TableHead>
                        <TableHead className="text-slate-400">Agent Name</TableHead>
                        <TableHead className="text-slate-400">Email</TableHead>
                        <TableHead className="text-slate-400 w-16">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(config?.mappings || []).length === 0 ? (
                        <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No mappings configured</TableCell></TableRow>
                      ) : (
                        (config?.mappings || []).map(m => (
                          <TableRow key={m.employee_code} className="border-slate-700">
                            <TableCell className="text-amber-400 font-mono font-bold">{m.employee_code}</TableCell>
                            <TableCell className="text-white">{m.agent_name || '—'}</TableCell>
                            <TableCell className="text-slate-300">{m.user_email}</TableCell>
                            <TableCell>
                              <button onClick={() => removeMapping(m.employee_code)} className="text-red-400 hover:text-red-300">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Unknown Punches */}
              {(config?.unknown_punches || []).length > 0 && (
                <Card className="bg-slate-800 border-red-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-red-400 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Unmapped Punches ({config.unknown_punches.length})
                      </div>
                      <Button variant="outline" size="sm" onClick={clearUnknownPunches} className="text-red-400 border-red-500/50">
                        Clear All
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 text-sm mb-3">These punches came from unknown employee codes. Map them above to capture attendance.</p>
                    <div className="space-y-2">
                      {config.unknown_punches.map((p, i) => (
                        <div key={i} className="flex items-center justify-between bg-slate-900 rounded-lg px-4 py-2">
                          <span className="text-amber-400 font-mono font-bold">{p.employee_code}</span>
                          <span className="text-slate-400 text-sm">{p.log_datetime}</span>
                          <span className="text-slate-500 text-sm">Device: {p.device_sn}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Logs Tab */}
          {activeTab === 'logs' && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Clock className="w-5 h-5 text-amber-400" />
                  Recent Biometric Punches
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Employee</TableHead>
                      <TableHead className="text-slate-400">Code</TableHead>
                      <TableHead className="text-slate-400">Punch Time</TableHead>
                      <TableHead className="text-slate-400">Device</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(config?.recent_logs || []).length === 0 ? (
                      <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No biometric punches received yet</TableCell></TableRow>
                    ) : (
                      (config?.recent_logs || []).map((log, i) => (
                        <TableRow key={i} className="border-slate-700">
                          <TableCell className="text-white">{log.user_email}</TableCell>
                          <TableCell className="text-amber-400 font-mono">{log.employee_code}</TableCell>
                          <TableCell className="text-slate-300">{log.log_datetime}</TableCell>
                          <TableCell className="text-slate-400">{log.device_name || log.device_sn}</TableCell>
                          <TableCell>
                            {log.is_late ? (
                              <span className="text-red-400 text-sm flex items-center gap-1">
                                <AlertTriangle className="w-3 h-3" /> Late ({log.late_time})
                              </span>
                            ) : (
                              <span className="text-emerald-400 text-sm flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" /> On Time
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Raw Debug Logs Tab */}
          {activeTab === 'rawlogs' && (
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                  Raw Data Received From Machine (Debug)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-400 text-sm mb-4">This shows the exact JSON data your biometric machine is sending. Use this to debug field mapping issues.</p>
                {(config?.raw_logs || []).length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No raw data received yet. Trigger a punch on the machine first.</p>
                ) : (
                  <div className="space-y-3">
                    {(config?.raw_logs || []).map((log, i) => (
                      <div key={i} className="bg-slate-900 rounded-lg p-4 border border-slate-700">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-amber-400 text-sm font-mono">Received: {log.received_at}</span>
                        </div>
                        <pre className="text-emerald-400 text-sm font-mono whitespace-pre-wrap overflow-auto">
                          {JSON.stringify(log.raw_data, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        <Footer />
      </div>

      {/* Add Device Dialog */}
      <Dialog open={showDeviceDialog} onOpenChange={setShowDeviceDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Server className="w-5 h-5 text-amber-400" /> Register Device
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={addDevice} className="space-y-4">
            <div>
              <Label className="text-slate-300">Device Serial Number *</Label>
              <Input value={deviceSn} onChange={e => setDeviceSn(e.target.value)} placeholder="e.g., BIO001" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Device Name</Label>
              <Input value={deviceName} onChange={e => setDeviceName(e.target.value)} placeholder="e.g., Main Gate Biometric" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">Location</Label>
              <Input value={deviceLocation} onChange={e => setDeviceLocation(e.target.value)} placeholder="e.g., Office Entrance" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowDeviceDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">Register</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Mapping Dialog */}
      <Dialog open={showMappingDialog} onOpenChange={setShowMappingDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Link2 className="w-5 h-5 text-amber-400" /> Map Employee Code
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={addMapping} className="space-y-4">
            <div>
              <Label className="text-slate-300">Employee Code (from machine) *</Label>
              <Input value={empCode} onChange={e => setEmpCode(e.target.value)} placeholder="e.g., EMP001" className="bg-slate-900 border-slate-600 text-white" />
            </div>
            <div>
              <Label className="text-slate-300">CRM User *</Label>
              <Select value={empEmail} onValueChange={setEmpEmail}>
                <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                  <SelectValue placeholder="Select an agent..." />
                </SelectTrigger>
                <SelectContent>
                  {users.filter(u => u.role !== 'admin').map(u => (
                    <SelectItem key={u.email} value={u.email}>
                      <div className="flex items-center gap-2">
                        <span>{u.agent_name}</span>
                        <span className="text-slate-400 text-xs">({u.role})</span>
                        {mappedEmails.includes(u.email) && <span className="text-emerald-400 text-xs">(mapped)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowMappingDialog(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 bg-amber-500 hover:bg-amber-600">Save Mapping</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BiometricSettings;
